import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import styles from "./ProductHero.module.scss";
import { getCDNImageUrl } from "@/utils/Common";
import { resolveIcon } from "./iconMap";
// Statically imported on purpose: this modal is a forwardRef component opened
// via ref, and next/dynamic does NOT forward refs (the ref would stay null and
// the modal would never open). Same constraint as LandingHero.
import ContactUsModalBtn from "@/components/Modal/ContactUs";

// `open`-prop controlled, so these can stay lazy.
const SampleRequestModal = dynamic(
  () => import("@/components/Products/SampleRequestModal"),
  { ssr: false },
);
const ContactUsFormModal = dynamic(
  () => import("@/components/us-website/ContactUsFormModal/ContactUsFormModal"),
  { ssr: false },
);

/**
 * Product-template hero (Figma `4671-16580` / mobile `5596-19252`): left copy +
 * check-chip row + CTAs, right product image.
 *
 * A SEPARATE component from `LandingHero`, not a variant of it. The product hero
 * needs the chip row INSIDE the left column between the description and the CTAs,
 * which `LandingHero` has no slot for; adding one would edit a component live on
 * all 18 supplier pages. Own stylesheet sharing zero selectors, so those pages
 * cannot regress by construction. The CTA/modal contract is deliberately kept
 * identical to `LandingHero`'s so configs read the same across both templates:
 *   "quote"       → ContactUsModalBtn
 *   "sample"      → SampleRequestModal
 *   "procurement" → ContactUsFormModal
 * A button with no `action` links to `href`.
 *
 * Unlike the supplier hero there is no description clamp: Figma's product hero
 * runs a 3-line intro at full length, so there is nothing to truncate and the
 * whole paragraph is in the crawlable HTML unconditionally.
 *
 * ⚠️ The two breakpoints disagree about button EMPHASIS, not just layout, so the
 * variant cannot simply be read from config:
 *   desktop `4671-16641` — Request Sample OUTLINED, Request Quote FILLED coral
 *   mobile  `5596-19821` — Request Sample FILLED coral, Request Quote OUTLINED
 *                          (#D50600 label on white), and BOTH carry a trailing
 *                          arrow that desktop omits.
 * So `variant` drives the desktop treatment and the mobile row inverts it in CSS
 * (`.mobile` swaps which class fills), which is why both rows render the same
 * config list rather than taking two.
 */

/**
 * Trailing arrow for the mobile CTAs. Inlined rather than an <img> because the
 * two mobile buttons need different arrow colours and `currentColor` does not
 * inherit into an external SVG loaded via <img>. Same approach as LandingHero.
 */
function ArrowRight({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
export default function ProductHero({
  heading = "",
  description,
  highlights = [],
  highlightIcon = "checkGreen",
  ctaText,
  ctaHref = "#lp-enquiry",
  ctaAction = "quote",
  desktopCtas,
  mobileCtas,
  quoteHeading = "Request Quote",
  quoteSubHeading = "Fill in the details required to get your quote.",
  quoteMessage,
  image,
  imageAlt,
  // Prefills the sample-request modal, which takes these verbatim. Unlike the
  // supplier hero (whose sample form is generic), this page is about ONE product,
  // so naming it here saves the buyer retyping it.
  productName,
  brandName,
  // ⚠️ Catalogue id of the prefilled product, forwarded to SampleRequestModal so
  // the submitted request carries `productId` instead of null. The modal has always
  // accepted this prop; this hero simply never passed it, so an autofilled product
  // (the whole point of a single-product page) submitted with no id while a
  // manually-searched one did — the inverse of what you'd expect.
  productId,
}) {
  const [sampleOpen, setSampleOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  // Ref-controlled modal: mount on first click, then open once the ref attaches.
  const [quoteMounted, setQuoteMounted] = useState(false);
  const quoteModalRef = useRef(null);

  useEffect(() => {
    if (quoteMounted) quoteModalRef.current?.setOpen(true);
  }, [quoteMounted]);

  const openAction = (action) => {
    if (action === "quote") {
      if (quoteMounted) quoteModalRef.current?.setOpen(true);
      else setQuoteMounted(true);
      return;
    }
    if (action === "sample") setSampleOpen(true);
    if (action === "procurement") setContactOpen(true);
  };

  const fallbackCtas = ctaText
    ? [{ text: ctaText, href: ctaHref, action: ctaAction }]
    : [];
  const desktopButtons =
    desktopCtas?.length > 0 ? desktopCtas : mobileCtas || fallbackCtas;
  const mobileButtons =
    mobileCtas?.length > 0 ? mobileCtas : desktopCtas || fallbackCtas;

  /**
   * Renders one CTA row. `variant` gives the DESKTOP emphasis (Figma
   * `4671-16641`); the mobile row inverts it in CSS, so the same config list
   * feeds both. `showArrow` is mobile-only — desktop buttons are text-only.
   */
  const renderCtas = (buttons, rowClass, showArrow) => {
    if (!buttons.length) return null;
    return (
      <div className={rowClass}>
        {buttons.map((btn, i) => {
          const cls =
            btn.variant === "filled" ? styles.ctaFilled : styles.ctaOutline;

          const content = (
            <>
              {btn.text}
              {showArrow && <ArrowRight className={styles.ctaIcon} />}
            </>
          );

          return btn.action ? (
            <button
              key={i}
              type="button"
              className={cls}
              onClick={() => openAction(btn.action)}
            >
              {content}
            </button>
          ) : (
            <a key={i} href={btn.href || ctaHref} className={cls}>
              {content}
            </a>
          );
        })}
      </div>
    );
  };

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.copy}>
          <h1 className={styles.heading}>{heading}</h1>
          {description && <p className={styles.desc}>{description}</p>}

          {highlights.length > 0 && (
            <ul className={styles.chips}>
              {highlights.map((chip, i) => {
                const label = typeof chip === "string" ? chip : chip.title;
                const icon = (typeof chip === "string" ? null : chip.icon) || highlightIcon;
                return (
                  <li className={styles.chip} key={i}>
                    <img
                      className={styles.chipIcon}
                      src={resolveIcon(icon)}
                      alt=""
                      aria-hidden="true"
                    />
                    {label}
                  </li>
                );
              })}
            </ul>
          )}

          {renderCtas(desktopButtons, styles.ctaRowDesktop, false)}
          {renderCtas(mobileButtons, styles.ctaRowMobile, true)}
        </div>

        {image && (
          <div className={styles.imageWrap}>
            <Image
              src={getCDNImageUrl(image)}
              alt={imageAlt || heading}
              fill
              priority
              sizes="(max-width: 900px) 100vw, 480px"
              style={{ objectFit: "cover" }}
            />
          </div>
        )}
      </div>

      {quoteMounted && (
        <ContactUsModalBtn
          ref={quoteModalRef}
          heading={quoteHeading}
          subHeading={quoteSubHeading}
          service="1"
          additionalInformationInitialValue={quoteMessage}
          // Attributes the quote request to this product. The modal forwards it to
          // its inner form, which until now DECLARED no `productId` param and so
          // silently dropped it — hence product arriving as null.
          productId={productId}
          isUsVersion
        />
      )}

      {sampleOpen && (
        <SampleRequestModal
          open={sampleOpen}
          onClose={() => setSampleOpen(false)}
          productName={productName}
          brandName={brandName}
          productId={productId}
        />
      )}

      {contactOpen && (
        <ContactUsFormModal
          open={contactOpen}
          onClose={() => setContactOpen(false)}
        />
      )}
    </section>
  );
}
